import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../data/mockData';
import { uploadFile, baseImageToOss, generateRandomCode } from '../utils/common';
import request from '../utils/request';
import { loadImagesFromAPI, ImageItem } from '../utils/imageUploadUtils';
import { PhotoIcon, TrashIcon, EyeIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import {OSS_BUCKET, OSS_BASE_URL} from '../config';
import LoginModal from '../components/LoginModal';
import {TOKEN_KEY} from '../utils/request';

const MyImagesLite: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useState(getCurrentUser());
  const [images, setImages] = useState<ImageItem[]>([]);
  const [selectedType, setSelectedType] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tagsMap, setTagsMap] = useState<Record<number, string[]>>({});
  const [selectedTag, setSelectedTag] = useState('');
  const [tagQuery, setTagQuery] = useState('');
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [selectedTagsList, setSelectedTagsList] = useState<string[]>([]);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const loadImages = async () => {
      setIsLoading(true);
      try {
        const token = typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
        if (!token) {
          setIsLoginOpen(true);
          setIsLoading(false);
          return;
        }
        const result = await loadImagesFromAPI(1, '', 'all', 100);
        setImages(result.images);
      } catch {
      } finally {
        setIsLoading(false);
      }
    };
    loadImages();
  }, []);

  const imageTypes = ['all', ...Array.from(new Set(images.map(img => img.type)))];
  const filteredImages = selectedType === 'all' ? images : images.filter(image => image.type === selectedType);
 

  const handleDeleteImage = async (imageId: number) => {
    try {
      const response = await request({ url: 'images/delete', method: 'DELETE', data: { id: imageId } });
      if (response.success) {
        setImages(images.filter(image => image.id !== imageId));
      }
    } catch {}
  };
 
  const handleSetTags = async (imageId: number) => {
    const input = window.prompt('Set tags (comma separated):', (tagsMap[imageId] || []).join(',')) || '';
    const tags = input.split(',').map(t => t.trim()).filter(Boolean);
    try {
      await request({ url: 'images/tags', method: 'POST', data: { id: imageId, tags } });
    } catch {}
    setTagsMap(prev => ({ ...prev, [imageId]: tags }));
  };

  const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1) {
      return '.jpg';
    }
    return filename.substring(lastDotIndex);
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!selectedTagsList || selectedTagsList.length === 0) {
      alert('请先选择或输入标签');
      return;
    }
    if (files && files.length > 0) {
      setIsUploading(true);
      try {
        const newImages: ImageItem[] = [];
        let maxId = Math.max(0, ...images.map(img => img.id));
        const filePromises = Array.from(files).map(async (file) => {
          if (!file.type.startsWith('image/')) return null;
          try {
            const base64String = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (event) => resolve(event.target?.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
            try {
              const uploadedName = await baseImageToOss(generateRandomCode() + getFileExtension(file.name), base64String, OSS_BUCKET);
              const newImage: ImageItem = {
                id: ++maxId,
                name: file.name.replace(/\.[^/.]+$/, ''),
                size: `${(file.size / (1024 * 1024)).toFixed(1)} MB`,
                uploadDate: new Date().toISOString().split('T')[0],
                type: 'Uploaded',
                isPublic: false,
                views: 0,
                downloads: 0,
                url: `${OSS_BASE_URL}${uploadedName}`
              };
              return newImage;
            } catch { 
              console.log('error')
              return null;
            }
          } catch {
            return null;
          }
        });
        const results = await Promise.all(filePromises);
        newImages.push(...results.filter((r): r is ImageItem => r !== null));
        if (newImages.length > 0) {
          try {
            const response = await request({ url: 'images/add', method: 'POST', data: { images: newImages,tags:selectedTagsList.join(',') } });
            if (response.success) {
              const result = await loadImagesFromAPI(1, '', 'all', 100,selectedTagsList);
              setImages(result.images);
            } else {
              setImages([...newImages, ...images]);
            }
          } catch {
            setImages([...newImages, ...images]);
          }
        }
      } catch {
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedTagsList || selectedTagsList.length === 0) {
      alert('请先选择或输入标签');
      return;
    }
    handleFileUpload(event.target.files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
      if (imageFiles.length > 0) {
        const dataTransfer = new DataTransfer();
        imageFiles.forEach(file => dataTransfer.items.add(file));
        handleFileUpload(dataTransfer.files);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8">
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2"> 
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg ${viewMode === 'grid' ? '' : 'text-gray-400 hover:text-gray-600'}`}
                    style={viewMode === 'grid' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                  >
                    <span className="text-lg">⊞</span>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg ${viewMode === 'list' ? '' : 'text-gray-400 hover:text-gray-600'}`}
                    style={viewMode === 'list' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                  >
                    <span className="text-lg">☰</span>
                  </button>
                </div>
                {selectedTagsList.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 ml-auto justify-end">
                    {selectedTagsList.map(t => (
                      <span key={t} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                        {t}
                        <button
                          className="ml-2 rounded-full px-1 hover:bg-gray-200"
                          onClick={async () => {
                            const next = selectedTagsList.filter(x => x !== t);
                            setSelectedTagsList(next);
                            setSelectedTag(next.join(','));
                            const result = await loadImagesFromAPI(1, '', 'all', 100, next);
                            setImages(result.images);
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <input
                    type="text"
                    value={tagQuery}
                    onChange={async (e) => {
                      const v = e.target.value;
                      setTagQuery(v);
                      try {
                        const resp = await request({ url: 'products/tags', method: 'GET', params: { keyword: v } });
                        const records = resp?.result?.records || [];
                        const tags = records.map((r: any) => String(r?.tagName || '').trim()).filter(Boolean);
                        setAvailableTags(Array.from(new Set(tags)));
                      } catch {}
                    }}
                    onKeyDown={async (e) => {
                      if (e.key !== 'Enter' || (e as any).nativeEvent?.isComposing) return;
                      const raw = tagQuery;
                      const parts = raw.split(/[\,\s]+/).map(s => s.trim()).filter(Boolean);
                      if (parts.length) {
                        for (const name of parts) {
                          try {
                            await request({ url: 'products/tags', method: 'POST', data: { tagName: name } });
                          } catch {}
                        }
                        const next = Array.from(new Set([...(selectedTagsList || []), ...parts]));
                        setSelectedTagsList(next);
                        setSelectedTag(next.join(','));
                        setAvailableTags(Array.from(new Set([...(availableTags || []), ...parts])));
                        const result = await loadImagesFromAPI(1, '', 'all', 100, next);
                        setImages(result.images);
                        setTagQuery('');
                      }
                    }}
                    placeholder="Search tags"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-56"
                  />
                {(availableTags.length > 0 || (tagQuery.trim() && availableTags.length === 0)) && (
                  <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-auto">
                      {availableTags.length === 0 && tagQuery.trim() && (
                        <button
                          type="button"
                          key={`__add_${tagQuery.trim()}`}
                          onClick={async () => {
                            const name = tagQuery.trim();
                            try {
                              await request({ url: 'products/tags', method: 'POST', data: { tagName: name } });
                            } catch {}
                            const next = Array.from(new Set([...(selectedTagsList || []), name]));
                            setSelectedTagsList(next);
                            setSelectedTag(next.join(','));
                            setAvailableTags(Array.from(new Set([...(availableTags || []), name])));
                            const result = await loadImagesFromAPI(1, '', 'all', 100, next);
                            setImages(result.images);
                            setTagQuery('');
                          }}
                          className="w-full text-left px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100"
                        >
                          Add new tag : {tagQuery.trim()}
                        </button>
                      )}
                      {(availableTags || []).map(t => {
                        const selected = selectedTagsList.includes(t);
                        return (
                          <button
                            type="button"
                            key={t}
                            onClick={async () => {
                              const next = selected
                                ? selectedTagsList.filter(x => x !== t)
                                : Array.from(new Set([...selectedTagsList, t]));
                              setSelectedTagsList(next);
                              setSelectedTag(next.join(','));
                              const result = await loadImagesFromAPI(1, '', 'all', 100, next);
                              setImages(result.images);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selected ? 'bg-gray-100' : ''}`}
                          >
                            <span className="mr-2 inline-block w-3">{selected ? '✓' : ''}</span>
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                <input type="file" accept="image/*" onChange={handleInputChange} className="hidden" id="image-upload-lite" multiple />
                <label htmlFor="image-upload-lite" className="px-3 py-2 bg-gray-800 text-white rounded cursor-pointer text-sm">Upload</label>
                <button
                  onClick={() => navigate('/bulk-creator', { state: { tags: selectedTagsList } })}
                  className="px-3 py-2 bg-gray-800 text-white rounded cursor-pointer text-sm"
                >
                  Create
                </button>
              </div>
            </div>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragOver ? 'border-gray-300 hover:border-gray-400' : 'border-gray-300 hover:border-gray-400'}`}
              style={isDragOver ? { borderColor: '#68aeb0', backgroundColor: '#f0f2f5' } : {}}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <CloudArrowUpIcon className={`w-12 h-12 mx-auto mb-4 ${isDragOver ? '' : 'text-gray-400'}`} />
              <p className="text-lg font-medium text-gray-900 mb-2">{isUploading ? 'Uploading...' : 'Upload Images'}</p>
              <p className="text-gray-500 mb-4">Drag and drop images here, or click to select files</p>
              <p className="text-sm text-gray-400">Supports JPG, PNG, GIF up to 10MB each • Multiple files supported</p>
            </div>
          </div>

          {isLoading ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <p className="text-gray-600">Loading images...</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-6 gap-4">
              {filteredImages.map((image) => (
                <div key={image.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative overflow-hidden">
                    {image.url ? (
                      <img src={image.url} alt={image.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }} />
                    ) : null}
                    <PhotoIcon className={`w-12 h-12 text-gray-400 ${image.url ? 'hidden' : ''}`} />
                    <div className="absolute top-2 right-2 flex space-x-1">
                      <button onClick={() => handleDeleteImage(image.id)} className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-red-50">
                        <TrashIcon className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2 group">
                      <div className="flex items-center space-x-2 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm truncate">{image.name}</h3>
                        <button
                          onClick={async () => {
                            const newName = window.prompt('Rename image:', image.name);
                            if (newName && newName.trim() !== image.name) {
                              try {
                                await request({ url: 'images/updateName', method: 'POST', data: { id: image.id, name: newName.trim() } });
                                setImages(prev => prev.map(img => img.id === image.id ? { ...img, name: newName.trim() } : img));
                              } catch {}
                            }
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-100"
                          title="Rename"
                        >
                          {/* Simple pencil unicode to avoid extra imports */}
                          <span className="text-gray-600 text-xs">✎</span>
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">{image.size} • Uploaded {new Date(image.uploadDate).toLocaleDateString()}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                        <span className="flex items-center"><EyeIcon className="w-3 h-3 mr-1" />{image.views}</span>
                        <span className="flex items-center"><span className="w-3 h-3 mr-1">⬇️</span>{image.downloads}</span>
                      </div>
                      <button onClick={() => handleSetTags(image.id)} className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700">Set Tags</button>
                    </div> 
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredImages.map((image) => (
                <div key={image.id} className="bg-white rounded-lg shadow-sm p-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {image.url ? (
                        <img src={image.url} alt={image.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling?.classList.remove('hidden'); }} />
                      ) : null}
                      <PhotoIcon className={`w-8 h-8 text-gray-400 ${image.url ? 'hidden' : ''}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                      <div className="flex-1 group">
                        <div className="flex items-center space-x-2 min-w-0">
                          <h3 className="font-semibold text-gray-900 mb-1 truncate">{image.name}</h3>
                          <button 
                           onClick={async () => { 
                             const newName = window.prompt('Rename image:', image.name); 
                             if (newName && newName.trim() !== image.name) { 
                               try {
                                 await request({ url: 'images/updateName', method: 'POST', data: { id: image.id, name: newName.trim() } });
                                 setImages(prev => prev.map(img => img.id === image.id ? { ...img, name: newName.trim() } : img)); 
                               } catch {}
                             } 
                           }} 
                           className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-gray-100" 
                           title="Rename" 
                         > 
                           <span className="text-gray-600 text-xs">✎</span> 
                         </button>
                        </div>
                          <p className="text-sm text-gray-500 mb-2">{image.type} • {image.size} • Uploaded {new Date(image.uploadDate).toLocaleDateString()}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span className="flex items-center"><EyeIcon className="w-4 h-4 mr-1" />{image.views} views</span>
                            <span className="flex items-center"><span className="w-4 h-4 mr-1">⬇️</span>{image.downloads} downloads</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button onClick={() => handleSetTags(image.id)} className="px-3 py-1 text-sm rounded-full bg-gray-100 text-gray-700">Set Tags</button>
                          <button onClick={() => handleDeleteImage(image.id)} className="p-2 text-red-400 hover:text-red-600"><TrashIcon className="w-4 h-4" /></button>
                        </div>
                      </div> 
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filteredImages.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <p className="text-gray-600 mb-6">No images found</p>
              <label htmlFor="image-upload-lite" className="inline-flex items-center px-6 py-3 text-white rounded-lg cursor-pointer transition-colors" style={{ backgroundColor: '#2e3a47' }} onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#3a4a57')} onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#2e3a47')}>
                <CloudArrowUpIcon className="w-5 h-5 mr-2" />
                Upload Your First Image
              </label>
            </div>
          )}
        </div>
      </div>
      <LoginModal 
        isOpen={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
      />
    </div>
  );
};

export default MyImagesLite;
